import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BrandListItem } from '@/api/models';
import { BrandQueryKeys } from './brandQueryKeys';

/**
 * Every brand, across as many `GET /brands/list` pages as that takes
 * (BOS-201). For a picker that must offer the whole set, like the Products
 * brand filter: `useBrandListQuery`'s default reads one page, which the
 * API caps at 200 brands.
 *
 * Unwraps the envelope (rulebook §10): `data` is the brands, and a failed
 * read is the query's `error`, so a caller can tell "no brands" from "the
 * brands didn't load".
 *
 * Keyed under `BrandQueryKeys.list()`, so a brand created, renamed or deleted
 * refetches it along with every page.
 */
export const useAllBrandsQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<BrandListItem[], ErrorResponse>({
        queryKey: BrandQueryKeys.allItems(),
        queryFn: async () => {
            const result = await apiClient.brands.getAllBrands();
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled,
        staleTime: 60_000,
    });
};
